/**
 * BonusTracker — prominent bonus progress display for the assistant dashboard.
 *
 * Shows current qualifying file count, progress bar with animated milestone
 * markers at the Bronze / Silver / Gold thresholds (from settings, falling
 * back to BONUS_THRESHOLD_DEFAULTS in bonusCalculator.js), tier colours,
 * and amount at stake.
 * Triggers MilestoneCelebration when a new tier is reached.
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Target } from 'lucide-react';
import { calculateBonusStatus, formatRands } from '../../utils/bonusCalculator';
import { MilestoneCelebration } from '../gamification/Celebrations';

export default function BonusTracker({ qualifyingCount, settings = {} }) {
  const bonus = calculateBonusStatus(qualifyingCount, settings);
  const prevTierRef = useRef(bonus.currentTier?.label || null);
  const [celebrationMilestone, setCelebrationMilestone] = useState(null);

  // Detect when a new tier is reached
  useEffect(() => {
    const currentLabel = bonus.currentTier?.label || null;
    const prevLabel = prevTierRef.current;

    if (currentLabel && currentLabel !== prevLabel && prevLabel !== null) {
      // New milestone reached!
      setCelebrationMilestone(bonus.currentTier);
    }

    prevTierRef.current = currentLabel;
  }, [bonus.currentTier]);

  // Progress segments for the three milestones
  const milestones = [
    { count: bonus.milestones.bronze.count, emoji: '🥉', label: 'Bronze', amount: bonus.milestones.bronze.amount, color: '#2EC4B6' },
    { count: bonus.milestones.silver.count, emoji: '🥈', label: 'Silver', amount: bonus.milestones.silver.amount, color: '#1B9AAA' },
    { count: bonus.milestones.gold.count, emoji: '🥇', label: 'Gold', amount: bonus.milestones.gold.amount, color: '#C9973A' },
  ];

  const maxCount = bonus.milestones.gold.count;
  const fillPercent = Math.min(100, (qualifyingCount / maxCount) * 100);

  // Determine the gradient colour based on progress
  let gradientColor = 'linear-gradient(90deg, var(--cp-success), var(--cp-secondary))';
  if (qualifyingCount >= bonus.milestones.gold.count) {
    gradientColor = 'linear-gradient(90deg, var(--opt-gold), #FFD700, var(--opt-gold))';
  } else if (qualifyingCount >= bonus.milestones.silver.count) {
    gradientColor = 'linear-gradient(90deg, var(--cp-secondary), var(--cp-primary))';
  } else if (qualifyingCount >= bonus.milestones.bronze.count) {
    gradientColor = 'linear-gradient(90deg, var(--cp-success), var(--cp-secondary))';
  }

  return (
    <>
      <div style={styles.container}>
        <div style={styles.header}>
          <Target size={16} color="#0F4C75" />
          <span style={styles.title}>Monthly Bonus</span>
          <span style={styles.count}>{qualifyingCount} qualifying files</span>
        </div>

        {/* Main progress bar */}
        <div style={styles.progressSection}>
          <div style={styles.track}>
            <motion.div
              style={{
                ...styles.fill,
                background: gradientColor,
              }}
              initial={{ width: 0 }}
              animate={{ width: `${fillPercent}%` }}
              transition={{ duration: 1, ease: [0.4, 0, 0.2, 1] }}
            />
            {/* Milestone markers */}
            {milestones.map((m) => {
              const pos = (m.count / maxCount) * 100;
              const reached = qualifyingCount >= m.count;
              return (
                <div key={m.label} style={{ ...styles.markerWrap, left: `${pos}%` }}>
                  <motion.div
                    style={{
                      ...styles.marker,
                      background: reached ? m.color : 'var(--cp-surface-alt)',
                      border: reached ? `2px solid ${m.color}` : '2px solid var(--cp-border)',
                      boxShadow: reached ? `0 2px 8px ${m.color}40` : 'none',
                    }}
                    animate={reached ? { scale: [1, 1.2, 1] } : {}}
                    transition={{ duration: 0.4 }}
                  >
                    <span style={{ fontSize: '0.7rem' }}>{m.emoji}</span>
                  </motion.div>
                  <span style={{
                    ...styles.markerLabel,
                    color: reached ? m.color : '#A0AABB',
                    fontWeight: reached ? 600 : 400,
                  }}>
                    {m.count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tier details */}
        <div className="bonus-tiers-row" style={styles.tiersRow}>
          {milestones.map((m) => {
            const reached = qualifyingCount >= m.count;
            return (
              <div key={m.label} style={{
                ...styles.tierCard,
                background: reached ? `${m.color}10` : '#FAFBFC',
                border: reached ? `1px solid ${m.color}30` : '1px solid var(--cp-surface-alt)',
              }}>
                <span style={styles.tierEmoji}>{m.emoji}</span>
                <div style={styles.tierInfo}>
                  <span style={{
                    ...styles.tierName,
                    color: reached ? m.color : '#A0AABB',
                  }}>{m.label}</span>
                  <span style={styles.tierAmount}>
                    {m.amount > 0 ? formatRands(m.amount) : '—'}
                  </span>
                </div>
                {reached && (
                  <motion.span
                    style={styles.tierCheck}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                  >
                    ✓
                  </motion.span>
                )}
              </div>
            );
          })}
        </div>

        {/* Status message */}
        <div style={styles.footer}>
          {bonus.nextTier ? (
            <p style={styles.footerText}>
              <strong>{bonus.filesUntilNext}</strong> more files to reach{' '}
              <strong>{bonus.nextTier.label}</strong>
              {bonus.nextTier.amount > 0 && ` (${formatRands(bonus.nextTier.amount)})`}
            </p>
          ) : (
            <motion.p
              style={{ ...styles.footerText, color: 'var(--opt-gold)', fontWeight: 600 }}
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              All milestones achieved — outstanding performance! 🎉
            </motion.p>
          )}
          {bonus.totalEarned > 0 && (
            <p style={styles.earned}>Total earned: {formatRands(bonus.totalEarned)}</p>
          )}
        </div>
      </div>

      {/* Milestone celebration overlay */}
      <MilestoneCelebration
        show={!!celebrationMilestone}
        milestone={celebrationMilestone}
        onDismiss={() => setCelebrationMilestone(null)}
      />
    </>
  );
}

const styles = {
  container: {
    background: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '12px',
    padding: '1rem',
    border: '1px solid rgba(15, 76, 117, 0.08)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginBottom: '0.75rem',
  },
  title: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    flex: 1,
  },
  count: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--cp-primary)',
    background: 'rgba(15, 76, 117, 0.08)',
    padding: '0.15rem 0.5rem',
    borderRadius: '999px',
  },
  progressSection: {
    marginBottom: '0.75rem',
  },
  track: {
    position: 'relative',
    height: '12px',
    background: 'var(--cp-surface-alt)',
    borderRadius: '999px',
    overflow: 'visible',
  },
  fill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: '999px',
  },
  markerWrap: {
    position: 'absolute',
    top: '-2px',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  marker: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s',
  },
  markerLabel: {
    fontSize: '0.6rem',
    marginTop: '1px',
  },
  tiersRow: {
    display: 'flex',
    gap: '0.4rem',
    marginBottom: '0.6rem',
    marginTop: '1.25rem',
  },
  tierCard: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.45rem 0.5rem',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  tierEmoji: {
    fontSize: '0.9rem',
    flexShrink: 0,
  },
  tierInfo: {
    flex: 1,
    minWidth: 0,
  },
  tierName: {
    display: 'block',
    fontSize: '0.7rem',
    fontWeight: 600,
  },
  tierAmount: {
    display: 'block',
    fontSize: '0.65rem',
    color: 'var(--cp-text-muted)',
  },
  tierCheck: {
    fontSize: '0.75rem',
    color: 'var(--cp-success)',
    fontWeight: 700,
  },
  footer: {
    textAlign: 'center',
  },
  footerText: {
    fontSize: '0.78rem',
    color: 'var(--cp-text-muted)',
    margin: 0,
  },
  earned: {
    fontSize: '0.7rem',
    color: 'var(--cp-primary)',
    fontWeight: 500,
    marginTop: '0.2rem',
  },
};
